import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCircle, LockKey, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlockedClientsSection = ({ userId, userRole }) => {
  const [blockedClients, setBlockedClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    loadBlockedClients();
  }, [userId]);

  const loadBlockedClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clients/blocked`, {
        params: { user_id: userId, role: userRole }
      });
      setBlockedClients(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load blocked clients:', error);
      if (![401, 403].includes(error.response?.status)) {
        toast.error('Failed to load blocked clients');
      }
      setLoading(false);
    }
  };

  const handleUnblock = async (customerId, customerName) => {
    if (!window.confirm(`Unblock ${customerName}?`)) return;

    try {
      await axios.post(`${API}/clients/unblock/${customerId}`);
      toast.success(`${customerName} unblocked successfully!`);
      loadBlockedClients(); // Reload list
    } catch (error) {
      console.error('Failed to unblock:', error);
      toast.error('Failed to unblock client');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-wa-text-secondary">Loading blocked clients...</p>
      </div>
    );
  }

  return (
    <div className="bg-wa-panel rounded-lg border border-wa-border p-6">
      <div className="flex items-center gap-3 mb-4">
        <LockKey size={24} weight="bold" className="text-red-600" />
        <h2 className="text-lg font-bold text-wa-text-primary">Blocked Clients</h2>
      </div>

      {blockedClients.length === 0 ? (
        <div className="text-center py-8 text-wa-text-secondary">
          <LockKey size={48} weight="light" className="mx-auto mb-3 text-gray-300" />
          <p>No blocked clients</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blockedClients.map((client) => (
            <div
              key={client.id}
              className="flex items-center gap-3 p-4 border border-red-100 rounded-lg bg-wa-hover hover:bg-wa-hover transition-colors"
            >
              <UserCircle size={40} weight="fill" className="text-wa-text-secondary flex-shrink-0" />
              
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-wa-text-primary">{client.username || 'Unnamed'}</p>
                <p className="text-sm text-wa-text-secondary">{client.phone}</p>
                {client.blocked_reason && (
                  <p className="text-xs text-red-600 font-semibold mt-1">Reason: {client.blocked_reason}</p>
                )}
                {client.blocked_by_name && (
                  <p className="text-xs text-slate-500 font-medium">
                    By: {client.blocked_by_name} ({client.blocked_by_role || 'handler'})
                  </p>
                )}
                <p className="text-xs text-wa-text-secondary">
                  Blocked: {client.blocked_at ? new Date(client.blocked_at).toLocaleString() : 'N/A'}
                </p>
              </div>

              <button
                onClick={() => handleUnblock(client.id, client.username || client.phone)}
                className="flex items-center gap-2 px-4 py-2 bg-wa-primary-strong text-white rounded-lg hover:bg-wa-primary-strong flex-shrink-0"
              >
                <CheckCircle size={18} weight="bold" />
                <span className="text-sm font-medium">Unblock</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlockedClientsSection;
